/**
 * Customer-facing transaction display rules for the ecommerce portal
 * (PD-14899).
 *
 * The portal transactions list reads like a bank statement, not like the
 * CRM's gateway ledger: authorizations that were captured or voided are
 * filtered out server-side (the capture row IS the charge the customer
 * sees), and the status a customer reads is based on whether the money
 * movement was CAPTURED — never on gateway settlement, which can lag days
 * behind and made completed ACH payments read as "Processing".
 */

export type PortalTransactionDisplayStatus =
	| 'Processing'
	| 'Succeeded'
	| 'Failed'
	| 'Voided'
	| 'Refunded';

/**
 * Map a raw transaction (type + gateway status) to the status the portal
 * shows. Rules, in order:
 * - Failed stays Failed; Voided stays Voided — terminal either way.
 * - An (unsuperseded) Authorization is a pending hold → Processing.
 * - A Refund shows Processing until the gateway finishes it, then Refunded.
 * - Any money-moving row (Capture / Sale / Cash / Check) that wasn't
 *   refunded shows Succeeded — including while the gateway still reports
 *   'Processing' toward settlement: the customer's account was charged the
 *   moment it was captured.
 */
export function getPortalTransactionDisplayStatus(
	type: string | null | undefined,
	status: string | null | undefined,
): PortalTransactionDisplayStatus {
	const normalizedStatus = (status ?? '').toLowerCase();
	if (normalizedStatus === 'failed' || normalizedStatus === 'error') {
		return 'Failed';
	}
	if (normalizedStatus === 'voided') {
		return 'Voided';
	}
	if (type === 'Authorization') {
		return 'Processing';
	}
	if (type === 'Refund') {
		return normalizedStatus.includes('processing') ? 'Processing' : 'Refunded';
	}
	if (normalizedStatus === 'refunded') {
		return 'Refunded';
	}
	return 'Succeeded';
}

/**
 * True when the row is money moving back TO the customer (a refund), so
 * the portal renders its amount as a credit (negative).
 */
export function isPortalCreditTransaction(
	type: string | null | undefined,
): boolean {
	return type === 'Refund';
}
